import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import '../../ui/ui.css';
import {
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import safeStorage from '../../utils/safeStorage';
import { applySunModeToDocument, readSunModePreference } from '../../utils/sunMode';
import { useNavTooltip } from '../ui/NavTooltip';
import { NavRailExpandableSection } from './NavRailExpandableSection';
import { ToolkitNavSubmenu } from '../../features/toolkit/components/ToolkitNavSubmenu';
import { CommunityNavSubmenu } from '../../features/community/components/CommunityNavSubmenu';
import { MobileSectionDial } from './MobileSectionDial';
import { NAV_SECTION_ICONS } from './navSectionIcons';
import { navProBadgeText } from '../../features/pro/navProBadge';

const YONA_BRAND_MARK_SRC = `${process.env.PUBLIC_URL || ''}/brand/yona-bear-2.png`;

function sectionIcon(id, size) {
  const Icon = NAV_SECTION_ICONS[id];
  return Icon ? <Icon size={size} /> : null;
}

function MobileNavSlot({
  active,
  label,
  title,
  onClick,
  badge,
  badgeTone,
  testId,
  proTestId,
  icon: Icon,
}) {
  const isPro = badgeTone === 'pro';
  return (
    <button
      type="button"
      className={`mobile-nav-slot${active ? ' mobile-nav-slot--active' : ''}`}
      title={title}
      aria-label={title}
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
      onClick={onClick}
    >
      <span className="mobile-nav-slot__icon" aria-hidden="true">
        {Icon ? <Icon size={24} /> : null}
      </span>
      <span className="mobile-nav-slot__label">{label}</span>
      {badge ? (
        <span
          className={`mobile-nav-slot__badge${isPro ? ' mobile-nav-slot__badge--pro' : ''}`}
          data-testid={isPro ? proTestId : undefined}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function DesktopNavLink({
  active,
  onClick,
  icon,
  label,
  title,
  tooltip,
  badge,
  expanded,
  className = '',
  iconWrapped = true,
  'aria-controls': ariaControls,
}) {
  const tip = tooltip || title || label;
  const { triggerProps, tooltip: tooltipNode } = useNavTooltip(tip);
  const accessibleName = title || label;

  return (
    <>
      <Button
        {...triggerProps}
        variant="link"
        className={`nav-rail-item ${className}${active ? ' nav-rail-item--active' : ''}`.trim()}
        aria-label={accessibleName}
        aria-current={active ? 'page' : undefined}
        aria-expanded={expanded === undefined ? undefined : expanded}
        aria-controls={ariaControls}
        onClick={onClick}
      >
        <span className="nav-rail-item-icon">
          {iconWrapped ? (
            <div className="nav-icon-wrapper">
              {icon}
              {badge}
            </div>
          ) : (
            <>
              {icon}
              {badge}
            </>
          )}
        </span>
        <span className="nav-rail-text-slot">
          <span className="nav-rail-label">{label}</span>
          {active ? <span className="nav-rail-active-dot" aria-hidden /> : null}
        </span>
      </Button>
      {tooltipNode}
    </>
  );
}

export function Navigation({ 
  currentPage, 
  onNavigate, 
  onShowFeatures, 
  showFeatureListButton = false,
  profilePhotoUrl,
  profileDisplayName = 'Profile',
  unreadMessagesTotal,
  unreadClubsBoardTotal = 0,
  unreadNotificationsTotal = 0,
  onOpenToolkit,
  onOpenCommunity,
  toolkitOpen = false,
  communityOpen = false,
  onToggleToolkit,
  onToggleCommunity,
  toolkitMenuActions = null,
  communityMenuActions = null,
  communityActive = false,
  overlayNav = false,
  onLogout,
  hasClubsEntitlement,
  hasToolkitEntitlement,
}) {
  const mobileNavRef = useRef(null);
  const [showSectionDial, setShowSectionDial] = useState(false);

  const readNavCollapsed = () => {
    try {
      return safeStorage.getItem('nav-rail-collapsed') === '1';
    } catch {
      return false;
    }
  };
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);

  useEffect(() => {
    try {
      safeStorage.setItem('nav-rail-collapsed', navCollapsed ? '1' : '0');
    } catch (_) {}
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty(
        '--nav-rail-current-width',
        navCollapsed ? 'var(--nav-rail-width-collapsed)' : 'var(--nav-rail-width-expanded)'
      );
    }
  }, [navCollapsed]);

  useEffect(() => {
    const updateVar = () => {
      const el = mobileNavRef.current;
      if (el) {
        // Measure the bar only — raised centre button is absolutely positioned.
        const h = el.offsetHeight || 0;
        document.documentElement.style.setProperty('--mobile-nav-height', `${h}px`);
      }
    };
    updateVar();
    window.addEventListener('resize', updateVar);
    return () => window.removeEventListener('resize', updateVar);
  }, []);

  useEffect(() => {
    applySunModeToDocument(readSunModePreference());
  }, []);

  const routeNavItems = useMemo(
    () => [
      { path: '/maps', icon: sectionIcon('ground', 24), title: 'My Ground', tooltip: 'My Ground' },
      { path: '/clubs', icon: sectionIcon('clubs', 24), title: 'Clubs', tooltip: 'Clubs' },
      { path: '/hunts', icon: sectionIcon('hunts', 28), title: 'Hunts', tooltip: 'Hunts' },
      { path: '/messages', icon: sectionIcon('messages', 24), title: 'Messages', tooltip: 'Messages' },
    ],
    []
  );
  const toolkitActive = currentPage === 'toolkit';

  const openSectionDial = useCallback(() => setShowSectionDial(true), []);
  const closeSectionDial = useCallback(() => setShowSectionDial(false), []);

  const handleMobileToolkit = useCallback(() => {
    const open = onToggleToolkit || onOpenToolkit;
    if (typeof open === 'function') {
      open();
      return;
    }
    if (typeof onNavigate === 'function') onNavigate('/toolkit');
  }, [onToggleToolkit, onOpenToolkit, onNavigate]);

  const clubsPro = navProBadgeText(hasClubsEntitlement);
  const toolkitPro = navProBadgeText(hasToolkitEntitlement);
  const clubsUnreadLabel =
    unreadClubsBoardTotal > 0
      ? unreadClubsBoardTotal > 99
        ? '99+'
        : String(unreadClubsBoardTotal)
      : null;
  const clubsBadge = clubsUnreadLabel || clubsPro;
  const clubsBadgeTone = clubsUnreadLabel ? 'unread' : clubsPro ? 'pro' : null;
  const messagesBadge =
    unreadMessagesTotal > 0
      ? unreadMessagesTotal > 99
        ? '99+'
        : String(unreadMessagesTotal)
      : null;

  return (
    <>
      {/* Desktop Navigation — collapsible rail (mockup-style) */}
      <div
        className={`nav-rail-shell nav-rail text-white d-none d-md-flex flex-column${overlayNav ? ' nav-rail-shell--overlay' : ''}`}
        data-collapsed={navCollapsed ? 'true' : 'false'}
      >
        <button
          type="button"
          className="nav-rail-collapse-fab"
          onClick={() => setNavCollapsed((c) => !c)}
          aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {navCollapsed ? <FaChevronRight size={12} /> : <FaChevronLeft size={12} />}
        </button>

        <div className="nav-rail-brand">
          <div className="nav-rail-brand-mark">
            <img src={YONA_BRAND_MARK_SRC} alt="Yona" className="nav-rail-brand-mark-img" />
          </div>
          <div className="nav-rail-brand-text">
            <div className="nav-rail-brand-title">Yona</div>
            <div className="nav-rail-brand-sub">Love the Land</div>
          </div>
        </div>

        <div className="nav-rail-scroll flex-grow-1 d-flex flex-column">
          {routeNavItems.map((item) => {
            let itemTooltip = item.tooltip;
            if (item.path === '/messages' && unreadMessagesTotal > 0) {
              itemTooltip = `Messages (${unreadMessagesTotal > 99 ? '99+' : unreadMessagesTotal} unread)`;
            } else if (item.path === '/clubs' && unreadClubsBoardTotal > 0) {
              itemTooltip = `Clubs (${unreadClubsBoardTotal > 99 ? '99+' : unreadClubsBoardTotal} unread)`;
            }

            return (
              <DesktopNavLink
                key={item.path}
                active={currentPage === item.path.slice(1)}
                onClick={() => onNavigate(item.path)}
                icon={item.icon}
                label={item.title}
                title={item.title}
                tooltip={itemTooltip}
                badge={
                  item.path === '/messages' && unreadMessagesTotal > 0 ? (
                    <span className="nav-unread-badge">{unreadMessagesTotal > 99 ? '99+' : unreadMessagesTotal}</span>
                  ) : item.path === '/clubs' && unreadClubsBoardTotal > 0 ? (
                    <span className="nav-unread-badge">{unreadClubsBoardTotal > 99 ? '99+' : unreadClubsBoardTotal}</span>
                  ) : item.path === '/clubs' && clubsPro ? (
                    <span className="nav-pro-badge" data-testid="nav-pro-badge-clubs">PRO</span>
                  ) : null
                }
              />
            );
          })}

          {onOpenToolkit && toolkitMenuActions ? (
            <NavRailExpandableSection
              open={toolkitOpen}
              submenuId="nav-rail-toolkit-submenu"
              trigger={
                <DesktopNavLink
                  active={toolkitActive || toolkitOpen}
                  expanded={toolkitOpen}
                  onClick={onToggleToolkit || onOpenToolkit}
                  icon={sectionIcon('toolkit', 22)}
                  label="Toolkit"
                  title="Toolkit"
                  tooltip="Plan & monitor your land"
                  aria-controls="nav-rail-toolkit-submenu"
                  badge={
                    toolkitPro ? (
                      <span className="nav-pro-badge" data-testid="nav-pro-badge-toolkit">PRO</span>
                    ) : null
                  }
                />
              }
            >
              <ToolkitNavSubmenu {...toolkitMenuActions} />
            </NavRailExpandableSection>
          ) : onOpenToolkit ? (
            <DesktopNavLink
              active={toolkitActive}
              onClick={onOpenToolkit}
              icon={sectionIcon('toolkit', 22)}
              label="Toolkit"
              title="Toolkit"
              tooltip="Plan & monitor your land"
              badge={
                toolkitPro ? (
                  <span className="nav-pro-badge" data-testid="nav-pro-badge-toolkit">PRO</span>
                ) : null
              }
            />
          ) : null}

          {onOpenCommunity && communityMenuActions ? (
            <NavRailExpandableSection
              open={communityOpen}
              submenuId="nav-rail-community-submenu"
              trigger={
                <DesktopNavLink
                  active={communityActive || communityOpen}
                  expanded={communityOpen}
                  onClick={onToggleCommunity || onOpenCommunity}
                  icon={sectionIcon('community', 22)}
                  label="Community"
                  title="Community"
                  tooltip="Connect with your crew"
                  aria-controls="nav-rail-community-submenu"
                />
              }
            >
              <CommunityNavSubmenu {...communityMenuActions} />
            </NavRailExpandableSection>
          ) : onOpenCommunity ? (
            <DesktopNavLink
              active={communityActive}
              onClick={onOpenCommunity}
              icon={sectionIcon('community', 22)}
              label="Community"
              title="Community"
              tooltip="Connect with your crew"
            />
          ) : null}
        </div>
      </div>

      {/* Mobile bottom bar: Ground · Clubs · FAB (More) · Hunts · Toolkit */}
      <div
        className="d-md-none mobile-nav-bar"
        style={{ zIndex: 1050 }}
        ref={mobileNavRef}
        data-testid="mobile-nav-bar"
        role="navigation"
        aria-label="Primary"
      >
        <MobileNavSlot
          label="GROUND"
          title="My Ground"
          active={currentPage === 'maps'}
          onClick={() => onNavigate('/maps')}
          testId="mobile-nav-slot-ground"
          icon={NAV_SECTION_ICONS.ground}
        />
        <MobileNavSlot
          label="CLUBS"
          title="Clubs"
          active={currentPage === 'clubs'}
          onClick={() => onNavigate('/clubs')}
          badge={clubsBadge}
          badgeTone={clubsBadgeTone}
          proTestId="mobile-nav-pro-clubs"
          testId="mobile-nav-slot-clubs"
          icon={NAV_SECTION_ICONS.clubs}
        />

        <div className="mobile-nav-center">
          <button
            type="button"
            className="mobile-nav-center-action"
            data-testid="mobile-nav-center-action"
            aria-label="More"
            aria-haspopup="dialog"
            aria-expanded={showSectionDial}
            onClick={openSectionDial}
          >
            <span className="mobile-nav-center-action__icon" aria-hidden="true">
              {sectionIcon('more', 28)}
            </span>
            {messagesBadge ? (
              <span
                className="mobile-nav-center-action__badge"
                data-testid="mobile-nav-fab-badge"
              >
                {messagesBadge}
              </span>
            ) : null}
          </button>
        </div>

        <MobileNavSlot
          label="HUNTS"
          title="Hunts"
          active={currentPage === 'hunts'}
          onClick={() => onNavigate('/hunts')}
          testId="mobile-nav-slot-hunts"
          icon={NAV_SECTION_ICONS.hunts}
        />
        <MobileNavSlot
          label="TOOLKIT"
          title="Toolkit"
          active={toolkitActive}
          onClick={handleMobileToolkit}
          badge={toolkitPro}
          badgeTone={toolkitPro ? 'pro' : null}
          proTestId="mobile-nav-pro-toolkit"
          testId="mobile-nav-slot-toolkit"
          icon={NAV_SECTION_ICONS.toolkit}
        />
      </div>

      <MobileSectionDial
        open={showSectionDial}
        onClose={closeSectionDial}
        currentPage={currentPage}
        onNavigate={onNavigate}
        onOpenToolkit={onToggleToolkit || onOpenToolkit}
        onOpenCommunity={onToggleCommunity || onOpenCommunity}
        unreadMessagesTotal={unreadMessagesTotal}
        unreadClubsBoardTotal={unreadClubsBoardTotal}
        hasClubsEntitlement={hasClubsEntitlement}
        hasToolkitEntitlement={hasToolkitEntitlement}
      />
    </>
  );
}
